import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Star, Clock, Shield } from 'lucide-react';
import DOMPurify from 'dompurify';

const LocationPage = () => {
  const { city, state } = useParams();
  
  // Major US cities for SEO targeting
  const cityData = {
    'new-york': {
      city: 'New York',
      state: 'NY', 
      population: '8.3M',
      description: 'Leading gift card exchange service in New York City',
      landmarks: ['Times Square', 'Central Park', 'Brooklyn Bridge'],
      zipCodes: ['10001', '10002', '10003', '10004']
    },
    'los-angeles': {
      city: 'Los Angeles',
      state: 'CA',
      population: '3.9M', 
      description: 'Trusted gift card buyers serving Los Angeles and surrounding areas',
      landmarks: ['Hollywood', 'Santa Monica', 'Beverly Hills'],
      zipCodes: ['90001', '90002', '90003', '90004']
    },
    'chicago': {
      city: 'Chicago',
      state: 'IL',
      population: '2.7M',
      description: 'Professional gift card exchange services in the Windy City',
      landmarks: ['Millennium Park', 'Navy Pier', 'Willis Tower'],
      zipCodes: ['60601', '60602', '60603', '60604']
    }
  };

  const currentCity = cityData[city] || {
    city: city?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Your City',
    state: state?.toUpperCase() || 'US',
    population: 'N/A',
    description: `Professional gift card exchange services in ${city?.replace(/-/g, ' ')}`,
    landmarks: [],
    zipCodes: []
  };

  useEffect(() => {
    // Analytics only (page title/description are handled by Helmet below)
    if (typeof gtag !== 'undefined') {
      gtag('event', 'page_view', {
        'page_title': `Sell Gift Cards ${currentCity.city} ${currentCity.state}`,
        'page_location': window.location.href,
        'event_category': 'local_seo'
      });
    }
  }, [city, state, currentCity]);

  const pageTitle = `Sell Gift Cards for Cash in ${currentCity.city}, ${currentCity.state} | Cashifygcmart`;
  const pageDescription = `Sell unused gift cards for cash in ${currentCity.city}, ${currentCity.state}. Up to 93% value, same-day payment. Trusted local gift card exchange service.`;
  const canonicalUrl = `https://cashifygcmart.com/location/${city}/${state}`;

  // Local business schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Cashifygcmart - ${currentCity.city}`,
    "description": currentCity.description,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": currentCity.city,
      "addressRegion": currentCity.state,
      "addressCountry": "US"
    },
    "areaServed": {
      "@type": "State", 
      "name": currentCity.state
    },
    "url": canonicalUrl,
    "sameAs": [
      "https://www.facebook.com/cashifygcmart",
      "https://www.twitter.com/cashifygcmart"
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="CashifyGCMart" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>
      {/* Local Business Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(localBusinessSchema)) }}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex justify-center items-center mb-4">
            <MapPin size={48} className="text-white mr-3" />
            <h1 className="text-4xl md:text-5xl font-bold">
              Sell Gift Cards in {currentCity.city}, {currentCity.state}
            </h1>
          </div>
          <p className="text-xl md:text-2xl opacity-90 mb-8">
            {currentCity.description} - Up to 93% of face value with same-day payments
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="bg-white/20 px-4 py-2 rounded-full">📍 Local Service</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">💰 Best Rates</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">⚡ Same Day</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Local Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="text-center bg-white rounded-lg shadow-md p-6">
            <div className="text-2xl font-bold text-blue-600">{currentCity.population}</div>
            <div className="text-sm text-gray-600">Population</div>
          </div>
          <div className="text-center bg-white rounded-lg shadow-md p-6">
            <div className="text-2xl font-bold text-green-600">93%</div>
            <div className="text-sm text-gray-600">Max Rate</div>
          </div>
          <div className="text-center bg-white rounded-lg shadow-md p-6">
            <div className="text-2xl font-bold text-purple-600">24hr</div>
            <div className="text-sm text-gray-600">Payout Time</div>
          </div>
          <div className="text-center bg-white rounded-lg shadow-md p-6">
            <div className="text-2xl font-bold text-orange-600">4.8⭐</div>
            <div className="text-sm text-gray-600">Customer Rating</div>
          </div>
        </div>

        {/* Why Choose Us Locally */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">
            Why {currentCity.city} Residents Choose Cashifygcmart
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center bg-white rounded-lg shadow-md p-6">
              <Shield size={48} className="text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Trusted Locally</h3>
              <p className="text-gray-600 text-sm">
                Serving {currentCity.city} area customers with secure, reliable service
              </p>
            </div>
            
            <div className="text-center bg-white rounded-lg shadow-md p-6">
              <Star size={48} className="text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Top Rated</h3>
              <p className="text-gray-600 text-sm">
                4.8/5 stars from satisfied {currentCity.state} customers
              </p>
            </div>
            
            <div className="text-center bg-white rounded-lg shadow-md p-6">
              <Clock size={48} className="text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Fast Processing</h3>
              <p className="text-gray-600 text-sm">
                Same-day payments for {currentCity.city} area residents
              </p>
            </div>
            
            <div className="text-center bg-white rounded-lg shadow-md p-6">
              <MapPin size={48} className="text-purple-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Local Knowledge</h3>
              <p className="text-gray-600 text-sm">
                Understanding {currentCity.city} market needs and preferences
              </p>
            </div>
          </div>
        </div>

        {/* Popular Gift Card Brands in Area */}
        <div className="mb-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">
            Popular Gift Cards in {currentCity.city}, {currentCity.state}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-bold text-lg mb-2">Amazon</h3>
              <div className="text-green-600 font-bold text-xl mb-2">88-93%</div>
              <p className="text-gray-600 text-sm">Most popular choice among {currentCity.city} residents</p>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-bold text-lg mb-2">Target</h3>
              <div className="text-green-600 font-bold text-xl mb-2">82-87%</div>
              <p className="text-gray-600 text-sm">High demand in {currentCity.state} markets</p>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-bold text-lg mb-2">Apple/iTunes</h3>
              <div className="text-green-600 font-bold text-xl mb-2">82-89%</div>
              <p className="text-gray-600 text-sm">Tech-savvy {currentCity.city} customers preferred choice</p>
            </div>
          </div>
        </div>

        {/* Local Landmarks/Areas Served */}
        {currentCity.landmarks.length > 0 && (
          <div className="mb-12 bg-blue-50 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Areas We Serve in {currentCity.city}</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {currentCity.landmarks.map((landmark, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <MapPin size={16} className="text-blue-600" />
                  <span className="text-gray-700">{landmark} area</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8 rounded-lg text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Sell Your Gift Cards in {currentCity.city}?
          </h2>
          <p className="text-xl mb-6">
            Join thousands of satisfied customers in {currentCity.state} who trust Cashifygcmart
          </p>
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <Link to="/rate-calculator" className="block sm:inline-block bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Get {currentCity.city} Rates
            </Link>
            <Link to="/form-submission" className="block sm:inline-block bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-colors border border-white">
              Start Selling Now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPage;